import React, { useState } from 'react'
import { emailTemplates, templateCategories } from '../data'
import { BlockRenderer } from './BlockRenderer'

// The classic template picker, kept exactly as it was for builds where the
// new template library is switched off (EMAIL_TEMPLATES_V2_VISIBLE): the new
// emails need the sending engine to use the campaign's emailHtml first.
export const ClassicTemplatePicker = ({
  isMobile, isDesignMode, userTemplates, drafts, previewMode, setPreviewMode,
  handleSelectTemplate, loadDraft, handleDeleteUserTemplate, BLANK_TEMPLATE,
}) => {
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const step = 0
  const galleryCategories = [
    ...templateCategories,
    ...(userTemplates.length ? [{ id: 'mine', label: 'My Templates' }] : []),
    ...(!isDesignMode && drafts.length ? [{ id: 'drafts', label: 'Drafts' }] : []),
  ]

  // Shared card chrome for user templates and drafts in the gallery
  const galleryThumbStyle = {
    height: isMobile ? '340px' : '360px',
    borderRadius: '14px',
    overflow: 'hidden',
    marginBottom: '12px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
    border: '1px solid #f0f0f2',
    backgroundColor: 'white',
    position: 'relative'
  }

  const renderUserTemplateCard = (t) => (
    <div key={t.id} onClick={() => handleSelectTemplate(t)} style={{ cursor: 'pointer', textAlign: 'left' }}>
      <div style={galleryThumbStyle}>
        <div style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%', height: '200%' }}>
          {(t.blocks || []).slice(0, 4).map((block, idx) => (
            <BlockRenderer key={idx} block={{ ...block, id: `utpl-prev-${idx}` }} />
          ))}
        </div>
        <button
          type="button"
          aria-label="Delete template"
          onClick={(e) => { e.stopPropagation(); handleDeleteUserTemplate(t.id) }}
          style={{
            position: 'absolute', top: '10px', right: '10px', width: '28px', height: '28px',
            borderRadius: '50%', border: 'none', background: 'rgba(26,26,30,0.55)', color: '#fff',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>
      <p style={{ fontSize: '11px', color: '#007EA0', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>My template</p>
      <p style={{ fontSize: '15px', color: '#29282D', margin: 0, fontWeight: '600', letterSpacing: '-0.1px' }}>{t.name}</p>
    </div>
  )

  const renderDraftCard = (d) => (
    <div key={d.id} onClick={() => loadDraft(d)} style={{ cursor: 'pointer', textAlign: 'left' }}>
      <div style={galleryThumbStyle}>
        {d.blocks.length ? (
          <div style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%', height: '200%' }}>
            {d.blocks.slice(0, 4).map((block, idx) => (
              <BlockRenderer key={idx} block={{ ...block, id: `draft-prev-${idx}` }} />
            ))}
          </div>
        ) : (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8E8E93', fontSize: '13px' }}>
            No design yet
          </div>
        )}
      </div>
      <p style={{ fontSize: '11px', color: '#A05A1F', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Draft</p>
      <p style={{ fontSize: '15px', color: '#29282D', margin: 0, fontWeight: '600', letterSpacing: '-0.1px' }}>{d.name}</p>
    </div>
  )

  return (
    <>
      {/* Step 0: Template Selection */}
      {step === 0 && !previewTemplate && (
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: isMobile ? 'auto' : 'calc(100vh - 110px)' }}>
          {/* Category Navigation — sidebar on desktop, horizontal chips on mobile */}
          {isMobile ? (
            <div style={{
              backgroundColor: 'white',
              borderBottom: '1px solid #f0f0f0',
              position: 'sticky',
              top: 0,
              zIndex: 50
            }}>
              <div style={{
                display: 'flex',
                gap: '8px',
                padding: '12px 16px',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}>
                {galleryCategories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: selectedCategory === cat.id ? '#29282D' : '#f3f4f6',
                      color: selectedCategory === cat.id ? 'white' : '#6b7280',
                      border: 'none',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      minHeight: '36px',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div style={{
              width: '220px',
              backgroundColor: 'white',
              borderRight: '1px solid #f0f0f2',
              padding: '32px 0',
              position: 'fixed',
              height: 'calc(100vh - 64px)',
              overflowY: 'auto'
            }}>
              <p style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', color: '#9ca3af', margin: '0 0 12px', padding: '0 24px' }}>Templates</p>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: '2px', padding: '0 16px' }}>
                {galleryCategories.map(cat => {
                  const active = selectedCategory === cat.id
                  return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    style={{
                      padding: '10px 16px',
                      textAlign: 'left',
                      backgroundColor: active ? '#f7f7f8' : 'transparent',
                      border: 'none',
                      borderRadius: '9px',
                      fontSize: '14px',
                      color: active ? '#29282D' : '#8e8e93',
                      fontWeight: active ? '600' : '500',
                      letterSpacing: active ? '-0.1px' : '0',
                      cursor: 'pointer',
                      transition: 'background 0.15s ease, color 0.15s ease'
                    }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = '#29282D' }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = '#8e8e93' }}
                  >
                    {cat.label}
                  </button>
                )})}
              </nav>
            </div>
          )}

          {/* Template Grid */}
          <div style={{
            flex: 1,
            padding: isMobile ? '20px 16px' : '40px 56px',
            marginLeft: isMobile ? 0 : '220px',
            backgroundColor: '#fafafa',
            minHeight: isMobile ? 'auto' : 'calc(100vh - 64px)',
            overflowY: 'auto'
          }}>
            {/* Header */}
            <div style={{ maxWidth: '860px', margin: isMobile ? '0 0 16px' : '0 auto 24px' }}>
              <h1 style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 700, letterSpacing: '-0.5px', color: '#29282D', margin: '0 0 6px' }}>Start with a template</h1>
              <p style={{ fontSize: '14px', color: '#8e8e93', margin: 0, lineHeight: 1.5 }}>Pick a design you love, then make it yours. Every template is fully editable.</p>
            </div>
            {/* Drafts: pick up where you left off */}
            {selectedCategory === 'all' && !isDesignMode && drafts.length > 0 && (
              <div style={{ maxWidth: '860px', margin: '0 auto 28px' }}>
                <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#29282D', margin: '0 0 14px' }}>Your drafts</h2>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))', gap: isMobile ? '20px' : '32px' }}>
                  {drafts.slice(0, 2).map(renderDraftCard)}
                </div>
                <h2 style={{ fontSize: '15px', fontWeight: 700, color: '#29282D', margin: '28px 0 0' }}>Templates</h2>
              </div>
            )}

            {/* Template Cards.
                minmax(0, 1fr) not 1fr: a grid item defaults to min-width auto,
                so the scaled preview inside forces the card wider than its
                track and the design gets clipped mid-image on a phone. */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))',
              gap: isMobile ? '20px' : '32px',
              maxWidth: '860px',
              margin: '0 auto'
            }}>
              {selectedCategory === 'mine' && userTemplates.map(renderUserTemplateCard)}
              {selectedCategory === 'drafts' && drafts.map(renderDraftCard)}
              {selectedCategory === 'all' && userTemplates.slice(0, 2).map(renderUserTemplateCard)}
              {selectedCategory === 'all' && (
                <button
                  onClick={() => handleSelectTemplate(BLANK_TEMPLATE)}
                  style={{
                    padding: 0, border: 'none', borderRadius: '12px', backgroundColor: 'transparent',
                    cursor: 'pointer', textAlign: 'left'
                  }}
                >
                  <div style={{
                    height: isMobile ? '340px' : '360px',
                    borderRadius: '14px',
                    marginBottom: '12px',
                    border: '1.5px dashed #D6D6DC',
                    backgroundColor: '#fff',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px'
                  }}>
                    <span style={{
                      width: '52px', height: '52px', borderRadius: '50%', backgroundColor: '#F2F2F7',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#4B4B52'
                    }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    </span>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#4B4B52' }}>Start from scratch</span>
                    <span style={{ fontSize: '12.5px', color: '#8E8E93', maxWidth: '210px', textAlign: 'center', lineHeight: 1.5 }}>A blank canvas with just your footer. Build it your way.</span>
                  </div>
                  <p style={{ fontSize: '11px', color: '#8E8E93', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>Blank</p>
                  <p style={{ fontSize: '15px', color: '#29282D', margin: 0, fontWeight: '600', letterSpacing: '-0.1px' }}>Blank email</p>
                </button>
              )}
              {emailTemplates
                .filter(t => selectedCategory === 'all' || t.category?.toLowerCase() === selectedCategory.toLowerCase())
                .map((template) => (
                <button
                  key={template.id}
                  onClick={() => setPreviewTemplate(template)}
                  style={{
                    padding: 0,
                    border: 'none',
                    borderRadius: '12px',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)'
                  }}
                  onMouseEnter={(e) => { if (!isMobile) e.currentTarget.style.transform = 'translateY(-4px)' }}
                  onMouseLeave={(e) => { if (!isMobile) e.currentTarget.style.transform = 'translateY(0)' }}
                >
                  <div style={{
                    height: isMobile ? '340px' : '360px',
                    borderRadius: '14px',
                    overflow: 'hidden',
                    marginBottom: '12px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                    border: '1px solid #f0f0f2',
                    backgroundColor: 'white'
                  }}>
                    <div style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%', height: '200%' }}>
                      {template.blocks?.slice(0, 4).map((block, idx) => (
                        <BlockRenderer key={idx} block={{ ...block, id: `preview-${idx}` }} />
                      ))}
                    </div>
                  </div>
                  <p style={{ fontSize: '11px', color: '#007EA0', margin: '0 0 4px 0', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>
                    {template.category}
                  </p>
                  <p style={{ fontSize: '15px', color: '#29282D', margin: 0, fontWeight: '600', letterSpacing: '-0.1px' }}>
                    {template.name}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Template Preview */}
      {step === 0 && previewTemplate && (
        <div style={{ minHeight: 'calc(100vh - 110px)', backgroundColor: 'white' }}>
          {/* Back to the template grid */}
          <div style={{ padding: '14px 16px 0' }}>
            <button
              type="button"
              onClick={() => setPreviewTemplate(null)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px 8px 10px',
                border: '1px solid #ECECF0',
                borderRadius: '100px',
                background: '#fff',
                color: '#4B4B52',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'inherit'
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
              All templates
            </button>
          </div>
          {isMobile ? (
            /* Mobile: stacked layout with sticky CTA */
            <div style={{ paddingBottom: '100px' }}>
              {/* Template info header */}
              <div style={{ padding: '20px 16px 16px' }}>
                <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  {previewTemplate.category}
                </p>
                <h2 style={{ fontSize: '22px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 8px 0' }}>
                  {previewTemplate.name}
                </h2>
                <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6', margin: 0 }}>
                  {previewTemplate.description}
                </p>
              </div>

              {/* Device Toggle — centered */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', padding: '8px 16px 16px' }}>
                <button
                  onClick={() => setPreviewMode('desktop')}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: previewMode === 'desktop' ? '#f3f4f6' : 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: previewMode === 'desktop' ? '#1a1a1a' : '#d1d5db',
                    cursor: 'pointer',
                    minHeight: '44px',
                    minWidth: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                </button>
                <button
                  onClick={() => setPreviewMode('mobile')}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: previewMode === 'mobile' ? '#f3f4f6' : 'transparent',
                    border: 'none',
                    borderRadius: '8px',
                    color: previewMode === 'mobile' ? '#1a1a1a' : '#d1d5db',
                    cursor: 'pointer',
                    minHeight: '44px',
                    minWidth: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                    <line x1="12" y1="18" x2="12" y2="18"/>
                  </svg>
                </button>
              </div>

              {/* Preview email — full width on mobile */}
              <div style={{ padding: '0 16px' }}>
                <div
                  style={{
                    width: '100%',
                    maxWidth: previewMode === 'mobile' ? '320px' : '100%',
                    margin: '0 auto',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                    transition: 'max-width 0.3s ease'
                  }}
                >
                  {previewTemplate.blocks?.map((block, idx) => (
                    <BlockRenderer key={idx} block={{ ...block, id: `preview-${idx}` }} />
                  ))}
                </div>
              </div>

              {/* Sticky bottom CTA */}
              <div style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '12px 16px',
                paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
                backgroundColor: 'white',
                borderTop: '1px solid #f0f0f0',
                zIndex: 50
              }}>
                <button
                  onClick={() => handleSelectTemplate(previewTemplate)}
                  style={{
                    width: '100%',
                    padding: '14px 28px',
                    backgroundColor: '#29282D',
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    minHeight: '48px'
                  }}
                >
                  Customise this template
                </button>
              </div>
            </div>
          ) : (
            /* Desktop: side-by-side layout */
            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '48px 40px' }}>
              <div style={{ display: 'flex', gap: '80px', alignItems: 'flex-start', justifyContent: 'center' }}>
                {/* Preview */}
                <div style={{ width: '480px', flexShrink: 0 }}>
                  <div
                    style={{
                      width: previewMode === 'mobile' ? '320px' : '480px',
                      margin: '0 auto',
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                      overflow: 'hidden',
                      transition: 'width 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.12)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.08)' }}
                  >
                    {previewTemplate.blocks?.map((block, idx) => (
                      <BlockRenderer key={idx} block={{ ...block, id: `preview-${idx}` }} />
                    ))}
                  </div>
                </div>

                {/* Info */}
                <div style={{ width: '340px', position: 'sticky', top: '24px' }}>
                  <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    {previewTemplate.category}
                  </p>
                  <h2 style={{ fontSize: '28px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 16px 0' }}>
                    {previewTemplate.name}
                  </h2>
                  <p style={{ fontSize: '15px', color: '#6b7280', lineHeight: '1.7', margin: '0 0 24px 0' }}>
                    {previewTemplate.description}
                  </p>

                  {/* Device Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
                    <button
                      onClick={() => setPreviewMode('desktop')}
                      style={{
                        padding: '8px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: previewMode === 'desktop' ? '#1a1a1a' : '#d1d5db',
                        cursor: 'pointer',
                        minHeight: '44px',
                        minWidth: '44px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                        <line x1="8" y1="21" x2="16" y2="21"/>
                        <line x1="12" y1="17" x2="12" y2="21"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => setPreviewMode('mobile')}
                      style={{
                        padding: '8px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: previewMode === 'mobile' ? '#1a1a1a' : '#d1d5db',
                        cursor: 'pointer',
                        minHeight: '44px',
                        minWidth: '44px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                        <line x1="12" y1="18" x2="12" y2="18"/>
                      </svg>
                    </button>
                  </div>

                  <button
                    onClick={() => handleSelectTemplate(previewTemplate)}
                    style={{
                      padding: '14px 28px',
                      backgroundColor: '#29282D',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      minHeight: '48px'
                    }}
                  >
                    Customise this template
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default ClassicTemplatePicker
